import type { ProductPlan } from "@/features/products/types"

export type DurationUnit = "day" | "week" | "month" | "year" | "lifetime" | "one_time"

export interface Duration {
  count: number
  unit: DurationUnit
}

/**
 * A plan is a trial when it charges a reduced price today and renews at a
 * different recurring price afterwards (the mapper only sets `recurringPrice`
 * in that case).
 */
export function isTrialPlan(plan: ProductPlan): boolean {
  return plan.recurringPrice != null && plan.recurringPrice !== plan.price
}

/** The crossed-out reference price: recurring price for trials, previous price otherwise. */
export function strikePrice(plan: ProductPlan): number | null {
  const reference = isTrialPlan(plan) ? plan.recurringPrice : plan.previousPrice
  return reference != null && reference > plan.price ? reference : null
}

/** Discount percentage — explicit `discountPercent`, else derived from the strike price. */
export function savingsPercent(plan: ProductPlan): number {
  if (plan.discountPercent && plan.discountPercent > 0) return Math.round(plan.discountPercent)
  const reference = strikePrice(plan)
  if (reference) return Math.round((1 - plan.price / reference) * 100)
  return 0
}

/**
 * Parse a backend length code ("7d", "14d", "1m", "2m", "12m", "1y", "lifetime", "otp")
 * into a normalized { count, unit }. By default days divisible by 7 collapse to weeks
 * and months divisible by 12 collapse to years (mirrors the interval mapper); pass
 * `{ collapse: false }` to keep the raw unit (used for trial labels like "2-Day Trial").
 */
export function parseDuration(
  code?: string,
  options: { collapse?: boolean } = {},
): Duration | null {
  const { collapse = true } = options
  if (!code) return null
  const c = code.toLowerCase().trim()
  if (c === "lifetime") return { count: 1, unit: "lifetime" }
  if (c.includes("otp")) return { count: 1, unit: "one_time" }

  const value = Math.abs(Number(c.match(/-?\d+/)?.[0] ?? 0))
  if (!value) return null

  if (/y/.test(c)) return { count: value, unit: "year" }
  if (/m/.test(c)) {
    return collapse && value >= 12 && value % 12 === 0
      ? { count: value / 12, unit: "year" }
      : { count: value, unit: "month" }
  }
  if (/w/.test(c)) return { count: value, unit: "week" }
  if (/d/.test(c)) {
    return collapse && value % 7 === 0
      ? { count: value / 7, unit: "week" }
      : { count: value, unit: "day" }
  }
  return null
}

/** "day" → "day"/"days" for the given count. */
export function pluralizeUnit(unit: DurationUnit, count: number): string {
  if (unit === "lifetime" || unit === "one_time") return ""
  return count === 1 ? unit : `${unit}s`
}

const UNIT_TITLE: Record<DurationUnit, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
  lifetime: "Lifetime",
  one_time: "One-time",
}

/** Title-cased period label, e.g. "Weekly" / "Monthly" / "Every 2 Months" / "Lifetime". */
export function durationLabel(duration: Duration | null): string {
  if (!duration) return ""
  const { count, unit } = duration
  if (unit === "lifetime") return "Lifetime"
  if (unit === "one_time") return "One-time"
  if (count === 1) {
    return { day: "Daily", week: "Weekly", month: "Monthly", year: "Yearly" }[unit] ?? ""
  }
  return `Every ${count} ${pluralizeUnit(unit, count)}`
}

/** Trial label like "2-Day Trial" / "1-Week Trial" (raw unit, not collapsed). */
export function trialLabel(plan: ProductPlan): string {
  const duration = parseDuration(plan.trialLengthCode, { collapse: false })
  if (!duration) return "Trial"
  return `${duration.count}-${UNIT_TITLE[duration.unit]} Trial`
}

/**
 * Lowercase trial-duration prefix for inline copy — "7-day ", "1-week " (with a
 * trailing space so it slots straight before the word "trial"), or "" when the
 * plan has no trial. Single source for the trial-length wording shared by the
 * checkout gift banner and the consent/renewal disclaimers, so they can never
 * drift apart.
 */
export function trialDurationPhrase(plan: ProductPlan): string {
  const duration = parseDuration(plan.trialLengthCode, { collapse: false })
  return duration ? `${duration.count}-${duration.unit} ` : ""
}

/** Left-hand period label for a single plan: trial period for trials, billing period otherwise. */
export function planPeriodLabel(plan: ProductPlan): string {
  if (plan.interval === "one_time") return "One-time"
  if (isTrialPlan(plan)) return trialLabel(plan)
  return durationLabel(parseDuration(plan.lengthCode)) || plan.name
}

/**
 * Human renewal phrase derived from the plan's length code, e.g. "every week",
 * "every 2 months", "every year". Empty for one-time / lifetime plans.
 */
export function renewalPhrase(plan: ProductPlan): string {
  if (plan.interval === "one_time") return ""
  const duration = parseDuration(plan.lengthCode) ?? intervalToDuration(plan)
  if (!duration || duration.unit === "lifetime" || duration.unit === "one_time") return ""
  const { count, unit } = duration
  return count === 1 ? `every ${unit}` : `every ${count} ${pluralizeUnit(unit, count)}`
}

/** Fallback when there's no length code: derive a duration from the coarse interval. */
function intervalToDuration(plan: ProductPlan): Duration | null {
  switch (plan.interval) {
    case "daily":
      return { count: 1, unit: "day" }
    case "weekly":
      return { count: 1, unit: "week" }
    case "monthly":
      return { count: 1, unit: "month" }
    case "yearly":
      return { count: 1, unit: "year" }
    case "lifetime":
      return { count: 1, unit: "lifetime" }
    default:
      return null
  }
}
